<template>
  <div class="ocr-report-map-panel">
    <v-alert v-if="!filename || tableIndex == null" type="info" variant="tonal" density="compact">
      Select a report and table from the sidebar.
    </v-alert>
    <template v-else>
      <div class="ocr-report-map-toolbar">
        <div class="ocr-toolbar-row">
          <v-switch
            v-model="viewReportPage"
            density="compact"
            hide-details
            color="primary"
            label="View report page"
            class="ocr-map-view-report-switch"
            :class="{ 'ocr-map-view-report-switch--on': viewReportPage }"
          />
          <div class="ocr-field ocr-report-map-type-select">
            <label class="profile-menu-label">Table type</label>
            <v-select
              v-model="localTableType"
              :items="tableTypes"
              :menu-props="selectMenuProps"
              variant="outlined"
              density="compact"
              hide-details
            />
          </div>
        </div>
        <div class="ocr-toolbar-row">
          <v-btn size="small" variant="outlined" class="ocr-toolbar-btn" :loading="applyingType" @click="applyType">Apply type</v-btn>
          <v-btn size="small" color="primary" variant="flat" class="ocr-toolbar-btn" :loading="predicting" @click="runPredict">
            Predict
          </v-btn>
          <v-btn size="small" color="primary" variant="tonal" class="ocr-toolbar-btn" :loading="saving" @click="saveMapped">
            Save mapped
          </v-btn>
        </div>
      </div>
      <v-alert v-if="statusMessage" :type="statusType" variant="tonal" density="compact" class="ocr-report-map-status">
        {{ statusMessage }}
      </v-alert>

      <div class="ocr-report-map-workspace" :class="{ 'ocr-report-map-workspace--split': viewReportPage }">
        <OcrReportPagePreview
          v-if="viewReportPage"
          v-model:page-index="localPageIndex"
          :filename="filename"
          :max-page="maxPageIndex"
          :table-index="tableIndex"
          class="ocr-report-map-page-preview"
        />
        <div class="ocr-report-map-workspace-main">
          <OcrMappingStepper
            v-model="stepIndex"
            :saving="saving"
            @save-finish="saveAndFinish"
          />
          <v-alert
            v-if="showSavedEditWarning"
            type="warning"
            variant="tonal"
            density="compact"
            class="ocr-map-saved-warning"
          >
            Editing a previously saved table may overwrite saved data. Use Save &amp; finish on Review when done.
          </v-alert>
          <div v-if="loading" class="ocr-empty-cell">Loading table…</div>
          <template v-else-if="detail">
            <OcrEditableGrid
              v-if="currentStepId === 'edit'"
              :grid="editGrid"
              :saving="savingEdits"
              @save="saveEditedGrid"
            />
            <OcrReportColumnMap
              v-else-if="currentStepId === 'columns'"
              :filename="filename"
              :table-index="tableIndex"
              :source-grid="editGrid"
              :table-type="detail.table_type || ''"
              :list-revision="columnListRevision"
              @updated="onColumnMapUpdated"
              @status="onColumnMapStatus"
            />
            <OcrReportHeaderMap
              v-else-if="currentStepId === 'header'"
              :filename="filename"
              :table-index="tableIndex"
              :table-type="detail.table_type || ''"
              :list-revision="columnListRevision"
              @updated="onColumnMapUpdated"
              @status="onColumnMapStatus"
            />
            <div v-else-if="currentStepId === 'review'" class="ocr-map-review-step">
              <OcrReportValidationPanel
                ref="validationPanel"
                :filename="filename"
                :table-index="tableIndex"
                @updated="onColumnMapUpdated"
                @status="onColumnMapStatus"
              />
              <div class="ocr-map-review-preview">
                <OcrDataTable title="Mapped data" :rows="mappedRows" />
              </div>
            </div>
            <div v-else-if="currentStepId === 'saved'" class="ocr-map-saved-step">
              <OcrDataTable v-if="savedRows.length" title="Saved table data" :rows="savedRows" />
              <p v-else class="ocr-empty-cell">
                No saved snapshot yet. Run validation on Review, then Save &amp; finish or Save mapped.
              </p>
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import ocrApi, { formatOcrApiError } from "@/services/ocrApi";
import OcrDataTable from "@/components/layout/OCR/OcrDataTable.vue";
import OcrEditableGrid from "@/components/layout/OCR/OcrEditableGrid.vue";
import OcrMappingStepper from "@/components/layout/OCR/OcrMappingStepper.vue";
import OcrReportColumnMap from "@/components/layout/OCR/OcrReportColumnMap.vue";
import OcrReportHeaderMap from "@/components/layout/OCR/OcrReportHeaderMap.vue";
import OcrReportPagePreview from "@/components/layout/OCR/OcrReportPagePreview.vue";
import OcrReportValidationPanel from "@/components/layout/OCR/OcrReportValidationPanel.vue";

export default {
  name: "OcrReportMapPanel",
  components: {
    OcrDataTable,
    OcrEditableGrid,
    OcrMappingStepper,
    OcrReportColumnMap,
    OcrReportHeaderMap,
    OcrReportPagePreview,
    OcrReportValidationPanel,
  },
  props: {
    filename: { type: String, default: null },
    tableIndex: { type: Number, default: null },
    tableTypes: { type: Array, default: () => [] },
    maxPageIndex: { type: Number, default: 0 },
    pageIndex: { type: Number, default: 0 },
  },
  emits: ["updated", "update:pageIndex"],
  data() {
    return {
      loading: false,
      detail: null,
      localTableType: "",
      stepIndex: 0,
      viewReportPage: true,
      localPageIndex: 0,
      predicting: false,
      applyingType: false,
      saving: false,
      savingEdits: false,
      statusMessage: "",
      statusType: "info",
      columnListRevision: 0,
      selectMenuProps: { contentClass: "ocr-select-menu" },
    };
  },
  computed: {
    currentStepId() {
      const steps = ["edit", "columns", "header", "review", "saved"];
      return steps[this.stepIndex] || "edit";
    },
    showSavedEditWarning() {
      const st = this.detail?.table_status;
      return st === "Saved" || st === "Published";
    },
    editGrid() {
      if (!this.detail) return null;
      return this.detail.edited || this.detail.raw || { columns: [], data: [] };
    },
    mappedRows() {
      return this.gridToRows(this.detail?.mapped);
    },
    savedRows() {
      return this.gridToRows(this.detail?.saved);
    },
  },
  watch: {
    filename: { handler() { this.loadDetail(true); } },
    tableIndex: { handler() { this.loadDetail(true); } },
    pageIndex: {
      immediate: true,
      handler(v) {
        this.localPageIndex = v ?? 0;
      },
    },
    localPageIndex(v) {
      this.$emit("update:pageIndex", v);
    },
    stepIndex(v) {
      if (v === 3) {
        this.$nextTick(() => {
          this.$refs.validationPanel?.runValidation?.();
        });
      }
    },
  },
  mounted() {
    this.loadDetail(false);
  },
  methods: {
    gridToRows(grid) {
      if (!grid) return [];
      if (Array.isArray(grid)) return grid;
      if (grid.columns && grid.data) {
        return grid.data.map((row) => {
          const obj = {};
          grid.columns.forEach((col, i) => {
            obj[col] = row[i];
          });
          return obj;
        });
      }
      return [];
    },
    onColumnMapUpdated(table) {
      if (table) this.detail = { ...this.detail, ...table };
      this.$emit("updated");
    },
    onColumnMapStatus({ type, message }) {
      this.statusMessage = message;
      this.statusType = type;
    },
    async loadDetail(resetStep = false) {
      this.detail = null;
      this.statusMessage = "";
      if (!this.filename || this.tableIndex == null) return;
      this.loading = true;
      try {
        const { data } = await ocrApi.get(
          `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}`,
        );
        this.detail = data;
        this.localTableType = data.table_type || "";
        if (data.page != null) {
          this.localPageIndex = data.page;
        }
        if (resetStep) {
          const st = data.table_status;
          this.stepIndex = st === "Saved" || st === "Published" ? 4 : 0;
        }
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Could not load table.");
        this.statusType = "error";
      } finally {
        this.loading = false;
      }
    },
    async saveEditedGrid(grid) {
      this.savingEdits = true;
      this.statusMessage = "";
      try {
        const { data } = await ocrApi.put(
          `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}`,
          { grid },
        );
        this.detail = data.table;
        this.statusMessage = "Edited table saved.";
        this.statusType = "success";
        this.$emit("updated");
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Could not save edits.");
        this.statusType = "error";
      } finally {
        this.savingEdits = false;
      }
    },
    async runPredict() {
      this.predicting = true;
      this.statusMessage = "";
      try {
        const { data } = await ocrApi.post(
          `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}/predict`,
        );
        this.detail = data.table;
        this.localTableType = data.table?.table_type || "";
        this.columnListRevision += 1;
        this.statusMessage = data.matched ? "Table type predicted." : "Prediction completed (review steps).";
        this.statusType = data.matched ? "success" : "warning";
        this.$emit("updated");
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Predict failed.");
        this.statusType = "error";
      } finally {
        this.predicting = false;
      }
    },
    async applyType() {
      if (!this.localTableType) return;
      this.applyingType = true;
      try {
        const { data } = await ocrApi.put(
          `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}/type`,
          { table_type: this.localTableType },
        );
        this.detail = data;
        this.columnListRevision += 1;
        this.statusMessage = "Table type updated.";
        this.statusType = "success";
        this.$emit("updated");
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Could not set table type.");
        this.statusType = "error";
      } finally {
        this.applyingType = false;
      }
    },
    async saveMapped() {
      this.saving = true;
      try {
        const { data } = await ocrApi.post(
          `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.tableIndex}/save-mapped`,
        );
        this.detail = data.table;
        this.statusMessage = data.message || (data.ok ? "Saved." : "Save failed.");
        this.statusType = data.ok ? "success" : "warning";
        if (data.ok) this.stepIndex = 4;
        this.$emit("updated");
      } catch (e) {
        this.statusMessage = formatOcrApiError(e, "Save failed.");
        this.statusType = "error";
      } finally {
        this.saving = false;
      }
    },
    async saveAndFinish() {
      await this.saveMapped();
      if (this.statusType === "success" || this.statusType === "warning") {
        this.stepIndex = 4;
      }
    },
  },
};
</script>
